/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import { DRIVER_SCOPE } from "@/components/DetailsSection/components/DetailsContent/components/ScheduleQuickDownload/types";
import { useParseHtmlContent } from "@/Pages/Dashboard/shared/News";
import Typography from "@mui/material/Typography";

import { colors, InfoIcon } from "@dotnet/telematics-design-system";

import Checkbox from "../../CheckBox/Checkbox";
import Tooltip from "../../Tooltip/Tooltip";
import { useStyles } from "../styles";
import { SCHEDULE_PAYLOAD_NAMES } from "../types";

interface IncludeDriverDataCheckboxesProps {
  driverScope: DRIVER_SCOPE;
  handleChange: (name: SCHEDULE_PAYLOAD_NAMES, value: any) => void;
}

const IncludeDriverDataCheckboxes = ({
  driverScope,
  handleChange
}: IncludeDriverDataCheckboxesProps) => {
  const { parseContent } = useParseHtmlContent();
  const { t } = useTranslation();

  const { infoIconCSS, typographyTitleIconCSS } = useStyles();

  const confirmIfChecked = (fieldName: SCHEDULE_PAYLOAD_NAMES) => {
    const slot =
      fieldName === SCHEDULE_PAYLOAD_NAMES.Driver1Slot
        ? DRIVER_SCOPE.Slot1
        : DRIVER_SCOPE.Slot2;

    return [slot, DRIVER_SCOPE.Both].includes(driverScope);
  };

  return (
    <div data-testid="include-driver-data-checkboxes">
      <div css={css(typographyTitleIconCSS)}>
        <Typography variant="body2Medium" sx={{ color: colors.textSecondary }}>
          {t("Schedules##include driver data")}
        </Typography>

        <Tooltip
          title={parseContent(t(`Schedules##include driver data tooltip`))}
          size="medium"
          placement="top"
          hasArrow
        >
          <span>
            <InfoIcon
              data-testid="driver-data-info-icon"
              height={14}
              width={14}
              css={css(infoIconCSS)}
            />
          </span>
        </Tooltip>
      </div>

      <div
        css={css({
          display: "flex",
          flexDirection: "column",
          rowGap: "8px",
          paddingBottom: "8px"
        })}
      >
        <Checkbox
          id="driver_slot_1_checkbox"
          checked={confirmIfChecked(SCHEDULE_PAYLOAD_NAMES.Driver1Slot)}
          onChange={(_, value) =>
            handleChange(SCHEDULE_PAYLOAD_NAMES.Driver1Slot, value)
          }
          label={`${t("Schedules##driver number", { driverNum: 1 })}`}
        />

        <Checkbox
          id="driver_slot_2_checkbox"
          checked={confirmIfChecked(SCHEDULE_PAYLOAD_NAMES.Driver2Slot)}
          onChange={(_, value) =>
            handleChange(SCHEDULE_PAYLOAD_NAMES.Driver2Slot, value)
          }
          label={`${t("Schedules##driver number", { driverNum: 2 })}`}
        />
      </div>
    </div>
  );
};

export default IncludeDriverDataCheckboxes;
